// WIKI-LINKS-GRAPH: the docs' link graph, drawn with react-force-graph-2d (MIT, canvas, d3-force;
// loaded with the first graph on screen). Two views:
//   - `DocLocalGraph`, the Graph tab beside a doc (`pages.graph`): the doc and its neighbours,
//     1 hop by default, 2 on the toggle; links that point nowhere are faint nodes;
//   - `DocsGraph`, from the Docs screen (`pages.graphAll`): every doc the viewer may open, by
//     space, #tag and orphans, capped by the API (2,000 nodes, the best-linked first).
// A click on a node opens the doc. The API names only docs the viewer may open, so the graph never
// shows a hidden doc. Colours are the theme's tokens, read from the page (light and dark), and the
// layout freezes after a cooldown. Each graph also lists its docs as links for keyboards and
// screen readers.
import {
  Button,
  Input,
  NativeSelect,
  NativeSelectOption,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  useTheme,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ComponentType, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { ForceGraphProps, LinkObject, NodeObject } from "react-force-graph-2d";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { docsGraphQuery, pageGraphQuery } from "./work-queries";

type Graph = Awaited<ReturnType<Api["client"]["pages"]["graph"]>>;
type GraphNode = Graph["nodes"][number];

type Drawn = GraphNode & { x?: number; y?: number };

// `lazy` drops the component's node type parameter: given back here
const ForceGraph2D = lazy(() => import("react-force-graph-2d")) as unknown as ComponentType<
  ForceGraphProps<NodeObject<Drawn>, LinkObject<Drawn>>
>;

/** Above this many nodes the canvas stops answering the pointer (drag, hover) to stay fast. */
const POINTER_MAX = 3000;
const SPACE_TOKENS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"];

type Palette = {
  node: string;
  focus: string;
  missing: string;
  edge: string;
  label: string;
  spaces: string[];
};

/** The theme's colours for the canvas, read again when the theme changes. */
function usePalette(): Palette {
  const { resolved } = useTheme();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the tokens change with the theme.
  return useMemo(() => {
    const css = typeof window === "undefined" ? null : getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) => css?.getPropertyValue(name).trim() || fallback;
    return {
      // Fallbacks (no stylesheet, e.g. before it loads) are CSS system colours, never literals.
      node: v("--muted-foreground", "GrayText"),
      focus: v("--primary", "Highlight"),
      missing: v("--border-strong", "GrayText"),
      edge: v("--border", "GrayText"),
      label: v("--foreground", "CanvasText"),
      spaces: SPACE_TOKENS.map((t) => v(t, "GrayText")),
    };
  }, [resolved]);
}

/** The element's width, followed as it resizes. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // no layout yet (or none at all, as in tests): the aside's width until it measures
    const measure = () => setWidth(el.clientWidth || 288);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** The canvas and its accessible list of docs. */
export function LinkGraph({
  company,
  graph,
  height,
  focusId,
  label,
}: {
  company: CompanyAccess;
  graph: Graph;
  /** `tall`: the Docs screen's 520 px; `short`: the doc's aside, 288 px. */
  height: "short" | "tall";
  focusId?: string | undefined;
  label: string;
}) {
  const navigate = useNavigate();
  const palette = usePalette();
  const [box, width] = useWidth<HTMLDivElement>();
  const spaceIndex = useMemo(() => {
    const m = new Map<string, number>();
    for (const n of graph.nodes) if (n.space_id && !m.has(n.space_id)) m.set(n.space_id, m.size);
    return m;
  }, [graph]);
  // the library writes positions into the objects it is given: a fresh copy per graph
  const data = useMemo(
    () => ({
      nodes: graph.nodes.map((n) => ({ ...n })),
      links: graph.edges.map((e) => ({ source: e.source, target: e.target })),
    }),
    [graph],
  );
  const open = (id: string) =>
    void navigate({
      to: "/c/$slug/work/p/$pageId",
      params: { slug: company.slug, pageId: id },
      search: keepFocus,
    });
  const colour = (n: Drawn) =>
    n.missing
      ? palette.missing
      : n.id === focusId
        ? palette.focus
        : n.space_id && spaceIndex.size > 1
          ? (palette.spaces[(spaceIndex.get(n.space_id) ?? 0) % palette.spaces.length] ??
            palette.node)
          : palette.node;
  const docs = graph.nodes.filter((n) => !n.missing);
  return (
    <div data-slot="link-graph" className="flex flex-col gap-2">
      <div
        ref={box}
        role="img"
        aria-label={`${label}: ${docs.length} ${docs.length === 1 ? "doc" : "docs"}, ${graph.edges.length} ${graph.edges.length === 1 ? "link" : "links"}`}
        className={`overflow-hidden rounded-md border border-border bg-sunken ${height === "tall" ? "h-[520px]" : "h-72"}`}
      >
        {width > 0 ? (
          <Suspense fallback={<Skeleton className="h-full w-full" />}>
            <ForceGraph2D
              graphData={data}
              width={width}
              height={height === "tall" ? 520 : 288}
              backgroundColor="transparent"
              nodeId="id"
              nodeLabel={(n) => (n.missing ? `${n.title} (no such doc)` : n.title)}
              nodeVal={(n) => 1 + Math.min(n.degree, 12)}
              nodeRelSize={3}
              nodeColor={colour}
              linkColor={() => palette.edge}
              linkDirectionalArrowLength={graph.nodes.length > 400 ? 0 : 3}
              linkDirectionalArrowRelPos={1}
              nodeCanvasObjectMode={() => "after"}
              nodeCanvasObject={(n, ctx, scale) => {
                if (scale < 1.6 && n.id !== focusId) return;
                const size = 11 / scale;
                ctx.font = `${size}px sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "top";
                ctx.fillStyle = n.missing ? palette.node : palette.label;
                const r = Math.sqrt(1 + Math.min(n.degree, 12)) * 3;
                ctx.fillText(n.title.slice(0, 40), n.x ?? 0, (n.y ?? 0) + r + 1);
              }}
              onNodeClick={(n) => {
                if (!n.missing) open(n.id);
              }}
              enablePointerInteraction={graph.nodes.length <= POINTER_MAX}
              cooldownTicks={120}
              warmupTicks={graph.nodes.length > 500 ? 40 : 0}
              autoPauseRedraw
            />
          </Suspense>
        ) : null}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          {graph.truncated ? "The best-linked docs (more matched)" : "The docs in this graph"}
        </summary>
        <ul aria-label={`${label} docs`} className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0">
          {docs.map((n) => (
            <li key={n.id}>
              <Link
                to="/c/$slug/work/p/$pageId"
                params={{ slug: company.slug, pageId: n.id }}
                search={keepFocus}
                className="block truncate rounded-sm px-2 py-1 hover:bg-muted"
              >
                {n.title || "Untitled"}
                <span className="ml-1 text-xs text-muted-foreground tabular-nums">
                  {n.degree} {n.degree === 1 ? "link" : "links"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/** The Graph tab beside a doc: the doc and its neighbours, 1 or 2 hops. */
export function DocLocalGraph({
  api,
  company,
  pageId,
}: {
  api: Api;
  company: CompanyAccess;
  pageId: string;
}) {
  const [depth, setDepth] = useState<1 | 2>(1);
  const graph = useQuery(pageGraphQuery(api, company.id, pageId, depth));
  return (
    <section aria-label="Graph" className="flex flex-col gap-2">
      <ToggleGroup
        aria-label="Hops"
        variant="chip"
        value={[String(depth)]}
        onValueChange={(v: unknown[]) => {
          const next = v[0];
          if (next === "1" || next === "2") setDepth(Number(next) as 1 | 2);
        }}
      >
        <ToggleGroupItem value="1">1 hop</ToggleGroupItem>
        <ToggleGroupItem value="2">2 hops</ToggleGroupItem>
      </ToggleGroup>
      {graph.isPending ? (
        <Skeleton className="h-64" />
      ) : graph.isError || !graph.data ? (
        <p className="px-2 py-1 text-sm text-muted-foreground">The graph did not load.</p>
      ) : graph.data.nodes.length <= 1 ? (
        <p className="px-2 py-1 text-sm text-muted-foreground">
          No links yet. Type [[ in the doc to link another.
        </p>
      ) : (
        <LinkGraph
          company={company}
          graph={graph.data}
          height="short"
          focusId={pageId}
          label="Links around this doc"
        />
      )}
    </section>
  );
}

/** The docs' graph from the Docs screen: by space, #tag and orphans. */
export function DocsGraph({
  api,
  company,
  spaces,
  initialSpaceId,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  spaces: { id: string; name: string }[];
  initialSpaceId?: string | undefined;
  onClose: () => void;
}) {
  const [spaceId, setSpaceId] = useState(initialSpaceId ?? "");
  const [tagDraft, setTagDraft] = useState("");
  const [tag, setTag] = useState("");
  const [orphans, setOrphans] = useState<"show" | "hide" | "only">("show");
  useEffect(() => {
    const t = setTimeout(() => setTag(tagDraft.trim().replace(/^#/, "")), 300);
    return () => clearTimeout(t);
  }, [tagDraft]);
  const validTag = /^[\p{L}][\p{L}\p{N}_/-]*$/u.test(tag) ? tag : "";
  const graph = useQuery(
    docsGraphQuery(api, company.id, {
      space_id: spaceId || undefined,
      tag: validTag || undefined,
      orphans,
    }),
  );
  return (
    <section aria-label="Docs graph" data-slot="docs-graph" className="flex flex-col gap-3 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <NativeSelect
          size="sm"
          aria-label="Graph space"
          className="w-40 shrink-0"
          value={spaceId}
          onChange={(e) => setSpaceId(e.target.value)}
        >
          <NativeSelectOption value="">All spaces</NativeSelectOption>
          {spaces.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {s.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Input
          aria-label="Graph tag"
          placeholder="#tag"
          className="h-8 w-32"
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
        />
        <NativeSelect
          size="sm"
          aria-label="Orphans"
          className="w-40 shrink-0"
          value={orphans}
          onChange={(e) => setOrphans(e.target.value as "show" | "hide" | "only")}
        >
          <NativeSelectOption value="show">With orphans</NativeSelectOption>
          <NativeSelectOption value="hide">Without orphans</NativeSelectOption>
          <NativeSelectOption value="only">Orphans only</NativeSelectOption>
        </NativeSelect>
        <span className="text-xs text-muted-foreground tabular-nums" aria-live="polite">
          {graph.data
            ? `${graph.data.nodes.length} ${graph.data.nodes.length === 1 ? "doc" : "docs"}${graph.data.truncated ? " (the best-linked; more matched)" : ""}`
            : ""}
        </span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
          Back to the list
        </Button>
      </div>
      {graph.isPending ? (
        <Skeleton className="h-[480px]" />
      ) : graph.isError || !graph.data ? (
        <p className="text-sm text-muted-foreground">The graph did not load.</p>
      ) : graph.data.nodes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No docs match.</p>
      ) : (
        <LinkGraph company={company} graph={graph.data} height="tall" label="Docs graph" />
      )}
    </section>
  );
}
